import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { HubAction } from 'enums/HubAction';
import { MessageType } from 'enums/MessageType';
import { environment } from 'environments/environment';
import { SettlementInstallmentModel, SettlementModel } from 'models/generated/Entities.GormCrm.Settlement';
import { SettlementPaymentModel } from 'models/generated/Entities.GormCrm.SettlementPayment';
import { OwnerDtoModel, SettlementPdfDtoModel } from 'models/generated/Models.SettlementPdfDto';
import { firstValueFrom } from 'rxjs';
import { LiveDataTracker, TrackingContext } from 'services/data-tracking';
import { PushService } from 'services/push.service';
import { DateService } from './date.service';
import { LegalCaseProductPlanModel } from 'models/generated/Entities.GormCrm.LegalCaseProductPlan';
import { LegalCaseModel } from 'models/generated/Entities.GormCrm.LegalCase';

export class CompanyActiveSettlement {
	constructor(public companyId: string, public activeSettlement: SettlementModel | undefined) { }

	getProperties() {
		return [
			{ name: 'companyId', type: 'string' },
			{ name: 'activeSettlement', type: 'SettlementModel', isObject: true, new: () => { return new SettlementModel }, properties: () => { return SettlementModel.classProperties } }];
	}
}

@Injectable()
export class LegalCaseService {

	private legalCaseUrl = `${environment.gatewayUrl}/api/legal-cases`;
	private settlementUrl = `${environment.gatewayUrl}/api/settlements`;
	private settlementTracker = new LiveDataTracker<SettlementModel, { settlementId: string, companyId?: string }>(settlement => settlement.id!);
	private companySettlementTracker = new LiveDataTracker<CompanyActiveSettlement, { settlementId: string, companyId?: string }>(settlementInfo => settlementInfo.companyId);
	
	constructor(private readonly http: HttpClient, private readonly pushService: PushService, private readonly dateService: DateService) {
		this.pushService.onChanges<{ settlementId: string, companyId?: string }>(MessageType.SettlementChanges).subscribe(event => {
			const settlementId = event.data?.settlementId;
			const companyId = event.data?.companyId;
			if (settlementId) this.settlementTracker.refreshData(event, settlementId, () => this.getSettlement(settlementId));
			if (companyId) this.companySettlementTracker.refreshData({ action: HubAction.Update, data: event.data, fromCurrentUser: event.fromCurrentUser }, companyId, () => this.getSettlementByCompanyId(companyId)); // Uses "Update" because inserts and deletes represents update to the state of active settlement per company

		});
	}

	getSettlement(settlementId: string): Promise<SettlementModel> {
		return firstValueFrom(this.http.get<SettlementModel>(`${this.settlementUrl}/${settlementId}`));
	}

	getSettlements(trackingContext?: TrackingContext): Promise<SettlementModel[]> {
		return firstValueFrom(this.http.get<SettlementModel[]>(`${this.settlementUrl}`))
			.then(settlements => this.settlementTracker.trackArrayForComponent(trackingContext, settlements));
	}

	getLegalCasesByCompanyId(companyId: string): Promise<LegalCaseModel[]> {
		return firstValueFrom(this.http.get<LegalCaseModel[]>(`${this.legalCaseUrl}/company/${companyId}`));
	}

	getPdfUrl(settlementId: string): Promise<string> {
		return firstValueFrom(this.http.get(`${this.settlementUrl}/${settlementId}/pdf-url`, { responseType: 'text' }));
	}

	getSettlementByCompanyId(companyId: string, trackingContext?: TrackingContext): Promise<CompanyActiveSettlement> {
		return firstValueFrom(this.http.get<SettlementModel[]>(`${this.settlementUrl}/company/${companyId}`))
			.then(settlements => { return new CompanyActiveSettlement(companyId.toLowerCase(), settlements?.find(s => s.isActive)); })
			.then(settlementInfo => this.companySettlementTracker.trackObjectForComponent(trackingContext, settlementInfo));
	}

	createSettlement(settlement: SettlementModel): Promise<SettlementModel> {
		settlement.include(s => s.settlementProductPlans);
		return firstValueFrom(this.http.post<SettlementModel>(`${this.settlementUrl}`, settlement));
	}

	closeSettlement(settlementId: string): Promise<void> {
		return firstValueFrom(this.http.patch<void>(`${this.settlementUrl}/${settlementId}/close`, {}));
	}

	cancelSettlement(settlementId: string): Promise<void> {
		return firstValueFrom(this.http.patch<void>(`${this.settlementUrl}/${settlementId}/cancel`, {}));
	}

	createPayment(payment: SettlementPaymentModel): Promise<void> {
		return firstValueFrom(this.http.post<void>(`${this.settlementUrl}/${payment.settlementId}/payment`, payment));
	}

	approveSettlement(settlementId: string, pdf: SettlementPdfDtoModel): Promise<void> {
		if (!pdf.owner) {
			// until further notice, we'll just hardcode the owner info, in the future it will either be attahced to the product plans or come from configuration files
			pdf.owner = new OwnerDtoModel();
			pdf.owner.name = 'Group Online A/S';
			pdf.owner.vatNumber = '29414815';
			pdf.owner.address = 'Metalbuen 66';
			pdf.owner.city = 'Ballerup';
			pdf.owner.zipcode = '2750';
			pdf.owner.bankName = 'Danske Bank';
			pdf.owner.bankReg = '1234';
			pdf.owner.bankAccount = '1234567890';
		}
		pdf.include(sp => sp.owner);
		pdf.include(sp => sp.company);
		pdf.include(sp => sp.productPlans);
		pdf.include(sp => sp.accountingPlans);
		return firstValueFrom(this.http.post<void>(`${this.settlementUrl}/${settlementId}/approve`, pdf));
	}

	acceptSettlement(settlementId: string) {
		return firstValueFrom(this.http.patch<SettlementModel>(`${this.settlementUrl}/${settlementId}/accept`, null));
	}

	sendReminder(settlementId: string) {
		return firstValueFrom(this.http.post<void>(`${this.settlementUrl}/${settlementId}/reminder`, null));
	}

	generateInstallments(settlement: SettlementModel): Promise<SettlementInstallmentModel[]> {
		return firstValueFrom(this.http.post<SettlementInstallmentModel[]>(`${this.settlementUrl}/generate-installments`, { settlementProductPlans: settlement.settlementProductPlans, dueDate: settlement.dueDate, installments: settlement.installments }));
	}

	clientAcceptanceOverdue(settlement: SettlementModel) {
		if (!settlement.dateSentToClient || settlement.acceptedByClient) return false;
		var date = this.dateService.addDays(new Date(), -1);
		return settlement.dateSentToClient < date;
	}

	clientBehindPayment(settlement: SettlementModel) {
		if (!settlement.acceptedByClient) return false;
		var date = this.dateService.addDays(new Date(), -4);
		const filteredInstallments = settlement.settlementInstallments.filter(i => i.dueDate !== undefined && i.dueDate < date);
		const expectedPaymentSum = filteredInstallments.reduce((partialSum, spp) => partialSum + spp.amount, 0) ?? 0;
		return expectedPaymentSum > 0 && expectedPaymentSum > settlement.paymentSumInclVat;
	}

	createLegalCase(companyId: string, brandId: string, producPlanIds: string[], invoiceNumbersToHold: string[]) {
		const legalCase = new LegalCaseModel;
		legalCase.companyId = companyId;
		legalCase.brandId = brandId;
		legalCase.legalCaseProductPlans = producPlanIds.map(p => Object.assign(new LegalCaseProductPlanModel(), { productPlanId: p }));
		legalCase.include(l => l.legalCaseProductPlans);

		return firstValueFrom(this.http.post<LegalCaseModel>(`${this.legalCaseUrl}`, { legalCase, invoiceNumbersToHold }));
	}

	closeLegalCase(legalCaseId: string, invoiceNumbersToRemoveFromHold: string[]) {
		return firstValueFrom(this.http.patch<LegalCaseModel>(`${this.legalCaseUrl}/close/${legalCaseId}`, invoiceNumbersToRemoveFromHold));
	}
}
